import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Brain, Send, Key, Settings, AlertCircle, CheckCircle2, 
  Loader2, Sparkles, Copy, RefreshCw 
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// LLM Providers configuration
const LLM_PROVIDERS = [
  { 
    id: 'openai', 
    name: 'OpenAI', 
    models: ['gpt-4o', 'gpt-4o-mini', 'gpt-3.5-turbo'],
    keyPlaceholder: 'sk-...',
    docsUrl: 'https://platform.openai.com/api-keys'
  },
  { 
    id: 'anthropic', 
    name: 'Anthropic', 
    models: [
      'claude-sonnet-4-5-20250929',
      'claude-sonnet-4-6',
      'claude-opus-4-8',
      'claude-opus-4-7',
      'claude-haiku-4-5-20251001',
      'claude-3-opus',
      'claude-3-sonnet',
      'claude-3-haiku'
    ],
    keyPlaceholder: 'sk-ant-...',
    docsUrl: 'https://console.anthropic.com/settings/keys'
  },
  { 
    id: 'google', 
    name: 'Google AI', 
    models: ['gemini-pro', 'gemini-pro-vision'],
    keyPlaceholder: 'AIza...',
    docsUrl: 'https://makersuite.google.com/app/apikey'
  }
];

const AIAssistant = () => {
  const [query, setQuery] = useState('');
  const [logs, setLogs] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  
  // BYOK State
  const [selectedProvider, setSelectedProvider] = useState('openai');
  const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
  const [apiKey, setApiKey] = useState('');
  const [apiKeyConfigured, setApiKeyConfigured] = useState(false);
  const [savingKey, setSavingKey] = useState(false);

  useEffect(() => {
    loadSavedConfig();
  }, []);

  const loadSavedConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/settings/llm-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data) {
        setSelectedProvider(response.data.provider || 'openai');
        setSelectedModel(response.data.model || 'gpt-4o-mini');
        setApiKeyConfigured(response.data.has_key || false);
      }
    } catch (error) {
      console.log('No saved LLM config found');
    }
  };

  const handleSaveConfig = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }

    setSavingKey(true);
    try {
      await axios.post(
        `${API_URL}/api/settings/llm-config`,
        {
          provider: selectedProvider,
          model: selectedModel,
          api_key: apiKey
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setApiKeyConfigured(true);
      setApiKey(''); // Clear after saving
      toast.success('LLM configuration saved successfully');
      setShowSettings(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSavingKey(false);
    }
  };

  const handleAnalyze = async () => {
    if (!query.trim()) {
      toast.error('Please provide a query');
      return;
    }

    if (!apiKeyConfigured) {
      toast.error('Please configure your LLM API key first');
      setShowSettings(true);
      return;
    }

    setAnalyzing(true);
    setResult('');
    
    try {
      const response = await axios.post(
        `${API_URL}/api/ai/analyze`,
        { 
          query, 
          logs: logs || null,
          provider: selectedProvider,
          model: selectedModel
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setResult(response.data.analysis);
      toast.success('Analysis complete');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'AI analysis failed';
      toast.error(errorMsg);
      
      if (errorMsg.includes('API key') || errorMsg.includes('authentication')) {
        setApiKeyConfigured(false);
        setShowSettings(true);
      }
    } finally {
      setAnalyzing(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(result);
    toast.success('Copied to clipboard');
  };

  const currentProvider = LLM_PROVIDERS.find(p => p.id === selectedProvider);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold font-heading mb-2 flex items-center gap-2">
            <Brain className="w-8 h-8 text-purple-500" />
            AI Assistant
          </h2>
          <p className="text-muted-foreground">Analyze logs and security data with AI</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowSettings(!showSettings)}
          className="gap-2"
        >
          <Settings className="w-4 h-4" />
          {apiKeyConfigured ? 'Change LLM' : 'Configure API Key'}
        </Button>
      </div>

      {/* API Key Configuration Panel */}
      {showSettings && (
        <Card className="mb-6 border-purple-200 bg-purple-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Key className="w-5 h-5 text-purple-600" />
              Bring Your Own Key (BYOK)
            </CardTitle>
            <CardDescription>
              Configure your own LLM API key for AI analysis. Your key is encrypted and stored securely.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={selectedProvider} onValueChange={setSelectedProvider}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {LLM_PROVIDERS.map(provider => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currentProvider?.models.map(model => (
                      <SelectItem key={model} value={model}>
                        {model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>API Key</Label>
                <a
                  href={currentProvider?.docsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-purple-600 hover:underline"
                >
                  Get API key →
                </a>
              </div>
              <Input
                type="password"
                placeholder={currentProvider?.keyPlaceholder || 'Enter API key'}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              {apiKeyConfigured && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  API key configured. Enter a new key to update.
                </p>
              )}
            </div>

            <div className="flex gap-2">
              <Button onClick={handleSaveConfig} disabled={savingKey} className="gap-2">
                {savingKey ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-4 h-4" />
                )}
                Save Configuration
              </Button>
              <Button variant="outline" onClick={() => setShowSettings(false)}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Badge */}
      <div className="flex items-center gap-2 mb-4">
        {apiKeyConfigured ? (
          <Badge className="bg-green-100 text-green-700 border-green-200">
            <CheckCircle2 className="w-3 h-3 mr-1" />
            {currentProvider?.name} - {selectedModel}
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
            <AlertCircle className="w-3 h-3 mr-1" />
            No API key configured
          </Badge>
        )}
      </div>

      {/* Main Interface */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Security Analysis Query</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="mb-2 block">What would you like to analyze?</Label>
            <Input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="e.g., Find suspicious login attempts, analyze this malware hash, explain these firewall logs..."
              className="w-full"
            />
          </div>
          <div>
            <Label className="mb-2 block">Paste Logs or Data (Optional)</Label>
            <Textarea
              value={logs}
              onChange={(e) => setLogs(e.target.value)}
              placeholder="Paste your log files, IOC data, or security events here for analysis..."
              className="w-full h-48 font-mono text-sm"
            />
          </div>
          <Button 
            onClick={handleAnalyze} 
            disabled={analyzing || !apiKeyConfigured} 
            className="w-full gap-2"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Analyze with AI
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {result && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Brain className="w-5 h-5 text-purple-500" />
                AI Analysis
              </CardTitle>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={copyToClipboard}>
                  <Copy className="w-4 h-4 mr-1" />
                  Copy
                </Button>
                <Button variant="outline" size="sm" onClick={handleAnalyze}>
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Regenerate
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none dark:prose-invert">
              <div className="whitespace-pre-wrap bg-slate-50 dark:bg-slate-900 p-4 rounded-lg text-sm">
                {result}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIAssistant;
